import { z } from "zod";
import {
  bmiQuizV1,
  bmiQuizV12,
  bmiQuizV2,
  isInformationStep,
  MTC_MEN2_KEY,
  PRESCRIPTION_TRANSFER_KEY_F01,
  TYPE_1_DIABETES_KEY,
  type BmiQuizField,
} from "./bmi-quiz";
import { localeSchema, type Locale } from "./common";
import { healthProfileSummarySchema } from "./health-profile-summary";

// TES-818 point 1 (A-01, Emelie 2026-09-20) — F-01 as the coach's PERSISTING
// start context.
//
// "Coachen ska ha F-01-sammanfattningen kvar som beständig startkontext i patientvyn — vad patienten vill ha hjälp med, upplevda hinder, tidigare försök, önskat stöd och mål — med originalsvaren åtkomliga där coachen har behörighet."
//
// F-01 is answered once, before anyone has met her, and then it disappears:
// today the summary exists only on the patient's own result card and on her
// timeline. The coach opens a patient with no idea what she came for. This
// contract is the employee-side read of that same material.
//
// Two shapes are deliberately NOT used here. It is not `F01Status` (that
// carries her decision, the saved-card state and the re-confirmation clock —
// patient-app concerns), and it is not a raw answer blob: A-01 asks for
// something a coach reads question by question, so the wire already carries
// the localized question label and the localized option labels. The admin
// renders text, it does not own a copy of the quiz template.

export const f01AnswerAccessSchema = z.enum(["visible", "restricted"]);
export type F01AnswerAccess = z.infer<typeof f01AnswerAccessSchema>;

export const f01StartContextAnswerSchema = z.object({
  // The quiz field key — stable across template versions, so the admin can
  // key on it without parsing the label.
  key: z.string(),
  // The question as the patient was asked it, in the locale she answered in.
  label: z.string(),
  access: f01AnswerAccessSchema,
  // The answer, already resolved to option labels / units / scale words. One
  // entry per selection, so a multi-select renders as a list rather than a
  // comma-joined string the admin would have to split again.
  //
  // EMPTY on a `restricted` row, and that emptiness IS the neutral status
  // A-01 asks for: the row's presence says the patient answered the question,
  // and nothing says what she answered.
  values: z.array(z.string()),
});
export type F01StartContextAnswer = z.infer<typeof f01StartContextAnswerSchema>;

export const f01StartContextSchema = z.object({
  // `not_started` is a normal answer, not an error: a patient who signed up
  // before F-01 existed, or who has not reached it yet, has no start context
  // and the coach's panel renders its empty state. A 404 would have made the
  // admin treat a perfectly ordinary patient as a broken one.
  status: z.enum(["available", "not_started"]),
  // Which row this was read from. The web funnel's `Lead` predates the
  // patient account, so the coach can tell "answered before signing up" from
  // "answered in the app" — they carry different freshness.
  source: z.enum(["submission", "lead"]).nullable(),
  submittedAt: z.string().nullable(),
  templateVersion: z.number().int().nullable(),
  // The locale the labels and answers above are rendered in — the one the
  // patient answered in, not the employee's. The coach is reading back what
  // the patient was asked, and translating the question drifts from it.
  locale: localeSchema,
  summary: healthProfileSummarySchema.nullable(),
  answers: z.array(f01StartContextAnswerSchema),
});
export type F01StartContext = z.infer<typeof f01StartContextSchema>;

// ---------------------------------------------------------------------------
// The coach/doctor boundary
// ---------------------------------------------------------------------------

// Ö-58 (TES-475) ANSWERED:
// "Coachen ser bara coachningsspåret: coachchatt, reflektioner, välmående, relevanta incheckningar. Inte recept, fullständiga labbsvar eller klinisk fritext."
//
// F-01 is mostly NOT clinical — it asks about obstacles, previous attempts,
// impact on everyday life and goals, which is precisely the coaching material
// A-01 wants restored. Six of its questions are the exception, and they are
// the whole gate:
//
//   reproductiveStatus  — pregnancy / breastfeeding / trying. Health data in
//                         its own right, and the input to the early stop; the
//                         doctor decides what it means, not the coach.
//   prescriptionTransfer— "Har du just nu ett giltigt recept eller en pågående läkemedelsbehandling…".
//                         Literally a prescription.
//   weightMedHistory    — drug history, including
//                         "slutade på grund av besvär eller biverkningar".
//                         Medication + adverse effects.
//   knownMedications    — named drugs (legacy v1 sets only).
//   type1Diabetes,
//   mtcMen2             — TES-1164: a diagnosis, and a cancer history in her
//                         family. The doctor's material, never the coach's.
//
// Where the line is NOT drawn, and why, because both look like near misses:
//
//   weightKg / heightCm stay visible. The summary the coach is granted states
//   the BMI and the A/B/C/D band outright, and `CoachPatientDetail` already
//   carries the BMI-routed `patientGroup`. Hiding the inputs while shipping
//   the output is theatre, not a boundary.
//
//   desiredOutcomeOther and the other free-text fields stay visible. "Klinisk
//   fritext" means a clinician's note or an anamnesis answer; F-01's free text
//   is the patient saying what she wants to change — the literal subject of
//   A-01's "önskat stöd och mål".
export const F01_MEDICAL_ANSWER_KEYS: readonly string[] = [
  "reproductiveStatus",
  PRESCRIPTION_TRANSFER_KEY_F01,
  "weightMedHistory",
  "knownMedications",
  TYPE_1_DIABETES_KEY,
  MTC_MEN2_KEY,
];

const MEDICAL_KEYS = new Set(F01_MEDICAL_ANSWER_KEYS);

/**
 * The answer set with the medical questions removed.
 *
 * Used to build the COACH's summary. `buildHealthProfileSummary` branches on
 * `prescriptionTransfer`: an active transfer choice replaces the whole
 * reflection with the routing block
 * ("Du har uppgett att du redan använder behandling och vill att Ella tar över ditt recept…").
 * That block is not what A-01 asks for — it contains none of her obstacles,
 * attempts or goals, only what happens to her prescription next. Feeding the
 * redacted set makes the builder produce her ordinary reflection instead,
 * which is both the thing the coach was promised and the thing that discloses
 * nothing about a prescription.
 *
 * The doctor's summary is built from the untouched answers.
 */
export function redactF01MedicalAnswers<T>(
  answers: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(answers).filter(([key]) => !MEDICAL_KEYS.has(key)),
  );
}

// ---------------------------------------------------------------------------
// Rendering the answers question by question
// ---------------------------------------------------------------------------

// One index over both published templates, v2 first so a key that exists in
// both (howLongTried, methodsTried, biggestObstacles, emotionalEating,
// impactOnLife…) is labelled the way it is asked TODAY. v1-only fields keep
// their own entry so an old web lead still renders as questions rather than
// as keys.
const FIELD_ORDER: BmiQuizField[] = (() => {
  const seen = new Set<string>();
  const ordered: BmiQuizField[] = [];
  // TES-1658 — v12 in between: the six questions v13 stopped asking keep
  // their labels for the answer sets that have them.
  for (const field of [
    ...bmiQuizV2.fields,
    ...bmiQuizV12.fields,
    ...bmiQuizV1.fields,
  ]) {
    if (seen.has(field.key)) continue;
    seen.add(field.key);
    ordered.push(field);
  }
  return ordered;
})();

const FIELD_BY_KEY = new Map(FIELD_ORDER.map((f) => [f.key, f]));

// Answers reach us from a Json column, so the value is `unknown` however well
// the write path validated it. Normalise to strings and drop everything else:
// an object or a null in there is corruption, and rendering it would put the
// raw blob on screen — the one thing A-01's "fråga-för-fråga" rules out.
function toRawValues(value: unknown): string[] {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return [String(value)];
  }
  if (Array.isArray(value)) return value.flatMap(toRawValues);
  return [];
}

function renderValues(
  field: BmiQuizField,
  raw: string[],
  locale: Locale,
): string[] {
  if (field.options) {
    // An unrecognised value is shown as-is rather than dropped: it means the
    // option was renamed or retired, and silently losing the answer would let
    // a coach read a shorter story than the patient told.
    return raw.map(
      (v) => field.options!.find((o) => o.value === v)?.label[locale] ?? v,
    );
  }
  if (field.kind === "numeric") {
    return raw.map((v) => (field.unit ? `${v} ${field.unit}` : v));
  }
  if (field.kind === "scale_1_5" && field.stepLabels) {
    return raw.map((v) => {
      const word = field.stepLabels?.[Number(v) - 1]?.[locale];
      return word ? `${v} – ${word}` : v;
    });
  }
  return raw;
}

/**
 * The answer list for one caller role.
 *
 * Only questions the patient actually answered produce a row — a conditional
 * follow-up that never rendered is not "unanswered", it was never asked, and
 * listing it would invent a gap in her story.
 *
 * Information steps are skipped: by contract they "skapar inget svarsvärde".
 */
export function buildF01StartContextAnswers(input: {
  answers: Record<string, unknown>;
  locale: Locale;
  role: "coach" | "doctor";
}): F01StartContextAnswer[] {
  const { answers, locale, role } = input;
  const rows: F01StartContextAnswer[] = [];

  for (const field of FIELD_ORDER) {
    if (isInformationStep(field)) continue;
    const raw = toRawValues(answers[field.key]);
    if (raw.length === 0) continue;
    const restricted = role === "coach" && MEDICAL_KEYS.has(field.key);
    rows.push({
      key: field.key,
      label: field.label[locale],
      access: restricted ? "restricted" : "visible",
      values: restricted ? [] : renderValues(field, raw, locale),
    });
  }

  // Keys no published template knows — a retired question, or an answer set
  // written by a template version that has since been edited. They go to the
  // doctor only, and that is the fail-closed direction: a key we cannot look
  // up is a key we cannot classify as non-medical.
  if (role === "doctor") {
    for (const [key, value] of Object.entries(answers)) {
      if (FIELD_BY_KEY.has(key)) continue;
      const raw = toRawValues(value);
      if (raw.length === 0) continue;
      rows.push({ key, label: key, access: "visible", values: raw });
    }
  }

  return rows;
}
